import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, Utensils, ArrowUp, ArrowDown, Trash2, Plus, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

// Parse the paste-friendly bulk textarea:
//   • one option per line
//   • trim whitespace
//   • skip empty lines
//   • dedupe (case-insensitive) while preserving first-seen order
const parseBulk = (text) => {
  const seen = new Set();
  const out = [];
  for (const raw of (text || '').split(/\r?\n/)) {
    const m = raw.trim();
    if (!m) continue;
    const k = m.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(m);
  }
  return out;
};

/**
 * AdminRSVPSettings
 *
 * Edits the couple's wedding-wide meal choices. Two coordinated inputs:
 *
 *   1. A bulk paste-and-save textarea (one meal per line) — matches the
 *      couple's mental model of "paste from a Google Doc / iOS Notes".
 *   2. A live-editable list below with drag-free reorder (up/down arrows),
 *      inline edits, add, and delete. The two stay in sync — changes in
 *      either propagate to the shared draft state.
 *
 * The Admin editor is the single source of truth; leaving a party's
 * `meal_options` blank makes it inherit from this list at RSVP time.
 */
const AdminRSVPSettings = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [meals, setMeals] = useState([]);
  const [bulk, setBulk] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/rsvp-settings`, { params: { _: Date.now() } });
      const list = Array.isArray(r.data?.meal_options) ? r.data.meal_options : [];
      setInitial(clone(list));
      setMeals(clone(list));
      setBulk(list.join('\n'));
    } catch {
      setError('Could not load current meal options.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => (
    initial && JSON.stringify(meals) !== JSON.stringify(initial)
  ), [meals, initial]);

  // When the bulk textarea changes, mirror into the structured list.
  const onBulkChange = (v) => {
    setBulk(v);
    setMeals(parseBulk(v));
  };
  // When the structured list changes (arrows/edit/delete), mirror back
  // into the bulk textarea so both views stay in sync.
  const syncBulk = (next) => {
    setMeals(next);
    setBulk(next.join('\n'));
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= meals.length) return;
    const next = [...meals];
    [next[i], next[j]] = [next[j], next[i]];
    syncBulk(next);
  };
  const remove = (i) => syncBulk(meals.filter((_, k) => k !== i));
  const edit = (i, v) => {
    const next = [...meals];
    next[i] = v;
    setMeals(next);
    setBulk(next.join('\n'));
  };
  const add = () => syncBulk([...meals, '']);

  const save = async () => {
    // Re-normalize on save so trailing empty rows / duplicates from
    // inline edits don't hit the backend.
    const cleaned = parseBulk(meals.join('\n'));
    if (cleaned.length === 0) {
      setError('Please enter at least one meal option, one per line.');
      return;
    }
    setSaving(true); setError('');
    try {
      const r = await axios.put(`${API}/admin/rsvp-settings`, { meal_options: cleaned }, { headers: authHeader() });
      const list = Array.isArray(r.data?.meal_options) ? r.data.meal_options : cleaned;
      setInitial(clone(list));
      setMeals(clone(list));
      setBulk(list.join('\n'));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/rsvp-settings/reset`, {}, { headers: authHeader() });
      const list = Array.isArray(r.data?.meal_options) ? r.data.meal_options : [];
      setInitial(clone(list));
      setMeals(clone(list));
      setBulk(list.join('\n'));
      setResetOpen(false);
    } catch {
      setError('Failed to reset meal options.');
    } finally { setResetting(false); }
  };

  const revert = () => {
    setMeals(clone(initial || []));
    setBulk((initial || []).join('\n'));
    setError('');
  };

  if (loading || !initial) {
    return (
      <div className="bg-white rounded-xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading RSVP settings…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-rsvp-settings">
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-[#F8F5EF] text-[#C8A951] inline-flex items-center justify-center">
            <Utensils className="w-4 h-4" />
          </span>
          <div>
            <h2 className="font-serif text-xl text-[#3B2F2F]">Global Wedding Meal Options</h2>
            <p className="text-xs text-[#8A7A7A]">Edits go live on every guest's RSVP form immediately — no redeploy needed.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetOpen(true)}
            className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1"
            data-testid="rsvp-settings-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={revert} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="rsvp-settings-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving}
            className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60"
            data-testid="rsvp-settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>
      {error && <p className="text-red-500 text-xs" data-testid="rsvp-settings-error">{error}</p>}

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-2 block">Paste your menu (one option per line)</label>
          <Textarea
            rows={7}
            value={bulk}
            onChange={(e) => onBulkChange(e.target.value)}
            placeholder={"Chicken\nBeef\nFish\nVegetarian\nVegan"}
            className="font-mono text-sm"
            data-testid="rsvp-settings-bulk"
          />
          <p className="text-[11px] text-[#8A7A7A] mt-2">
            Blank lines are ignored. Duplicates are removed automatically.
            Each line becomes one selectable meal option for every attending guest during RSVP.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-[#3B2F2F]">Preview & reorder</h3>
            <p className="text-xs text-[#8A7A7A]">Guests will see these options in this exact order on the RSVP form.</p>
          </div>
          <Button onClick={add} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="rsvp-settings-add">
            <Plus className="w-4 h-4 mr-1" /> Add option
          </Button>
        </div>
        {meals.length === 0 && (
          <p className="text-sm text-[#8A7A7A] italic" data-testid="rsvp-settings-empty">
            No meal options yet — paste your menu above.
          </p>
        )}
        <ul className="space-y-2">
          {meals.map((m, i) => (
            <li key={i} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center p-3 rounded-lg border border-[#E5E0D8]"
                data-testid={`rsvp-settings-row-${i}`}>
              <span className="w-7 h-7 rounded-full bg-[#F8F5EF] text-[#8A7A7A] text-xs inline-flex items-center justify-center font-medium">
                {i + 1}
              </span>
              <Input
                value={m}
                onChange={(e) => edit(i, e.target.value)}
                placeholder="e.g. Chicken"
                data-testid={`rsvp-settings-input-${i}`}
              />
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0}
                  className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                  data-testid={`rsvp-settings-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === meals.length - 1}
                  className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                  data-testid={`rsvp-settings-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => remove(i)}
                  className="p-1.5 rounded-md text-red-500 hover:bg-red-50"
                  data-testid={`rsvp-settings-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-[#F8F5EF] rounded-xl p-4 text-xs text-[#5A4A4A]">
        <p>
          <strong className="text-[#3B2F2F]">How this connects to individual parties:</strong>{' '}
          Every party's <em>Meal Options</em> field remains available as a per-party override —
          leave it blank and that party will automatically use this global list.
        </p>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset meal options to defaults?</DialogTitle>
            <DialogDescription>
              This restores the built-in menu (Chicken, Beef, Fish, Vegetarian, Vegan).
              Cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button onClick={resetDefaults} disabled={resetting} className="bg-[#C8A951] hover:bg-[#B09240] text-white">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRSVPSettings;
